import {
  Box,
  Button,
  PhoneMockup,
  PhoneMockupBodyLayout,
  PhoneMockupLayout,
  Text,
  WidgetHeader,
  WidgetHeaderContent,
  WidgetStatus,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useEffect, useState } from 'react';
import { isOfframpBill } from '../../utils';

import {
  GetRefundModal,
  TopBar,
  TransactionDropdown,
  addStatusToTransactions,
} from '../../components';
import {
  useExpireBill,
  useRegisterHotjarPage,
  useReopenPayment,
  useStopPayment,
} from '../../hooks';

import { useWidgetContext } from '../../context/WidgetContext';
import { PageFields_PaymentExpiredPage_Fragment as PaymentRateExpiredPageField } from '../../graphql/generated';
import { ApiChainSymbols, binancePayAsBlockchain } from '../../utils';

interface PaymentRateExpiredProps {
  data: PaymentRateExpiredPageField['pageData'];
  onMadePaymentClick?: () => void;
}

const PaymentRateExpired = ({
  data,
  onMadePaymentClick,
}: PaymentRateExpiredProps) => {
  const {
    paymentExpiredConfirmedTransactions: transactions,
    paymentId,
    paymentExpiredExpiresAt: expiresAt,
    merchantName,
    paymentExpiredRefundable: refundable,
    paymentExpiredBlockchain: blockchain,
    billType,
  } = data;
  const id = getIdFromUrl();

  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const { reopenPayment, loading: reopenPaymentLoading } = useReopenPayment();
  const { stopPayment, loading: stopPaymentLoading } = useStopPayment();
  const { expireBill: requestRefund, loading: refundLoading } = useExpireBill();

  useRegisterHotjarPage('payment-rate-expired');

  const handleReopenPaymentClick = () => {
    reopenPayment({
      variables: {
        id,
        paymentId: paymentId || '',
        type: 'bill',
      },
    });
  };

  const handleRefundClick = () => {
    requestRefund({
      variables: {
        id,
        reason: 'refund',
      },
    });
  };

  const handleRestartPaymentClick = () => {
    stopPayment({
      variables: {
        id,
        paymentId: paymentId || '',
        reason: 'go_back',
      },
    });
  };

  const hasTransaction = transactions && transactions.length > 0;

  const { configureHeader } = useWidgetContext();

  useEffect(() => {
    configureHeader({
      id,
      expiresAt: expiresAt,
      merchantName,
      billType,
    });
  }, [expiresAt]);

  const isLightningNetwork = blockchain === ApiChainSymbols['LN-BTC'];
  const isBinancePayNetwork = blockchain === binancePayAsBlockchain;

  const requiresPaymentRestart = isLightningNetwork || isBinancePayNetwork;

  const isOffRamp = isOfframpBill(billType);

  const getStatusDescription = () => {
    if (hasTransaction) {
      return (
        <span data-test-id="has-tx-description">
          The previous rate is no longer valid, but we have detected a
          transaction.
        </span>
      );
    }

    if (requiresPaymentRestart) {
      return (
        <span data-test-id="requires-payment-restart-description">
          We couldn’t detect any transactions while the rate was locked.
        </span>
      );
    }

    return (
      <span data-test-id="no-tx-description">
        We couldn’t detect any transactions while the rate was locked. We are
        still looking for your {isOffRamp ? 'transaction' : 'payment'} on the
        blockchain.
      </span>
    );
  };

  const reopenPaymentButton = (
    <Button
      fullWidth
      type="button"
      loading={reopenPaymentLoading}
      onClick={handleReopenPaymentClick}
      data-test-id="reopen-payment-button"
    >
      get a new rate
    </Button>
  );

  const getActionButtons = () => {
    if (requiresPaymentRestart) {
      return (
        <Button
          size="large"
          variant="primary"
          fullWidth
          data-test-id="restart-payment-button"
          type="button"
          onClick={handleRestartPaymentClick}
          loading={stopPaymentLoading}
        >
          {isOffRamp ? 'Restart' : 'Restart payment'}
        </Button>
      );
    }

    if (hasTransaction) {
      if (refundable) {
        return (
          <>
            {reopenPaymentButton}
            <Button
              variant="ghost"
              fullWidth
              mt="8"
              loading={refundLoading}
              onClick={handleRefundClick}
              data-test-id="refund-button"
            >
              Get a refund
            </Button>
          </>
        );
      }

      return reopenPaymentButton;
    }

    return (
      <>
        {reopenPaymentButton}
        <Button
          variant="secondary"
          fullWidth
          onClick={onMadePaymentClick}
          data-test-id="made-payment-button"
          mt="8"
        >
          I’ve made a {isOffRamp ? 'transfer' : 'payment'}
        </Button>
      </>
    );
  };

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <WidgetHeader color="warning">
            <WidgetHeaderContent title="Exchange Rate Expired" />
          </WidgetHeader>
        }
      >
        <PhoneMockupBodyLayout
          header={
            <TopBar
              rightSlot={
                hasTransaction && (
                  <TransactionDropdown
                    transactions={addStatusToTransactions(
                      transactions,
                      'confirmed'
                    )}
                  />
                )
              }
            />
          }
          bodyCentered
          body={
            <Box>
              <WidgetStatus
                emoji="⌛️"
                title="Exchange rate expired"
                description={getStatusDescription()}
              />
              {requiresPaymentRestart && (
                <Text
                  as="div"
                  mt="8"
                  size="small"
                  color="vitaminC-500"
                  align="center"
                  data-test-id="requires-payment-restart-warning"
                >
                  {isOffRamp
                    ? 'The transfer details provided are no longer valid.'
                    : 'The payment details provided are no longer valid.'}
                  <br />
                  {isOffRamp
                    ? 'Please restart to try again.'
                    : 'Please restart the payment to try again'}
                </Text>
              )}
              {hasTransaction && refundable && !requiresPaymentRestart && (
                <Text
                  as="div"
                  align="center"
                  mt="8"
                  weight="600"
                  size="small"
                  color="royal-500"
                  data-test-id="refund-description"
                >
                  {isOffRamp
                    ? 'You can get a new rate and continue off ramping'
                    : 'You can get a new rate and continue with your purchase'}
                </Text>
              )}
            </Box>
          }
          footer={getActionButtons()}
        />
        {refundModalOpen && (
          <GetRefundModal
            isOffRamp={isOffRamp}
            onGetRefundClick={requestRefund}
            loading={refundLoading}
            onRequestClose={() => setRefundModalOpen(false)}
          />
        )}
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default PaymentRateExpired;
